import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Timestamp, collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";

interface BankDataRow {
  id: string;
  check: boolean;
  depositdate?: Timestamp | null;
  name: string;
  incash: number;
  orderNumber: string;
  userName: string;
  orderId: string | null;
  paymentsRefPath: string;
}

const PAGE_SIZE = 50;

const formatDate = (value?: Timestamp | null) => {
  if (!value) return "-";
  return value.toDate().toLocaleDateString("ja-JP");
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("ja-JP");
};

const formatStatus = (value: boolean) => (value ? "消込済" : "未処理");

export default function BankDataHistoryPage() {
  const [rows, setRows] = useState<BankDataRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailTarget, setDetailTarget] = useState<BankDataRow | null>(null);
  const [editingTarget, setEditingTarget] = useState<BankDataRow | null>(null);
  const [paymentsRefInput, setPaymentsRefInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      setError(null);
      try {
        const bankQuery = query(collection(db, "bankdata"), orderBy("depositdate", "desc"));
        const snapshot = await getDocs(bankQuery);
        const mapped = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as Record<string, any>;
          const paymentsRef = data.paymentsRef;
          const orderRef = data.OrderRef;

          const orderIdFromPaymentsRef = paymentsRef?.parent?.parent?.id;
          const orderIdFromOrderRef = orderRef?.id;
          const orderId = orderIdFromPaymentsRef || orderIdFromOrderRef || null;

          return {
            id: docSnap.id,
            check: Boolean(data.check),
            depositdate: data.depositdate ?? null,
            name: data.name || "-",
            incash: Number(data.incash ?? 0),
            orderNumber: data.orderNumber || "-",
            userName: data.userName || "-",
            orderId,
            paymentsRefPath: paymentsRef?.path || "",
          } satisfies BankDataRow;
        });
        setRows(mapped);
        setCurrentPage(1);
      } catch (fetchError: any) {
        const errorCode = fetchError?.code || fetchError?.name;
        if (errorCode === "permission-denied") {
          setError("権限エラーのためbankdataの取得に失敗しました。");
        } else {
          setError("bankdataの取得に失敗しました。");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRows();
  }, []);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedRows = useMemo(() => rows.slice(startIndex, startIndex + PAGE_SIZE), [rows, startIndex]);

  const parseDocPath = (path: string) => {
    const normalized = path.trim().replace(/^\/+|\/+$/g, "");
    if (!normalized) return null;
    const segments = normalized.split("/").filter(Boolean);
    if (segments.length < 2 || segments.length % 2 !== 0) return null;
    return doc(db, ...segments);
  };

  const handleStartEdit = (row: BankDataRow) => {
    setEditingTarget(row);
    setPaymentsRefInput(row.paymentsRefPath);
  };

  const handleSavePaymentsRef = async () => {
    if (!editingTarget) return;
    const paymentsRef = parseDocPath(paymentsRefInput);
    if (!paymentsRef) {
      alert("paymentsRefはドキュメントパス形式で入力してください（例: Order/xxxx/payments/yyyy）");
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "bankdata", editingTarget.id), { paymentsRef });
      const orderId = paymentsRef.parent?.parent?.id || null;
      setRows((prev) =>
        prev.map((row) =>
          row.id === editingTarget.id
            ? {
                ...row,
                paymentsRefPath: paymentsRef.path,
                orderId,
              }
            : row,
        ),
      );
      setEditingTarget(null);
    } catch (saveError) {
      console.error("Failed to save paymentsRef", saveError);
      alert("paymentsRefの保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("このbankdataドキュメントを削除しますか？")) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, "bankdata", id));
      setRows((prev) => prev.filter((row) => row.id !== id));
    } catch (deleteError) {
      console.error("Failed to delete bankdata", deleteError);
      alert("ドキュメントの削除に失敗しました。");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">入金履歴一覧</h2>
          <p className="text-sm text-gray-500">bankdata コレクションの履歴を表示します。</p>
        </div>
        <Link
          to="/admin/paymentmg"
          className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          入金管理へ戻る
        </Link>
      </div>

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">読み込み中...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-500">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">bankdata がありません。</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-emerald-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">ステータス</th>
                    <th className="px-3 py-2 text-left">入金日</th>
                    <th className="px-3 py-2 text-left">名義</th>
                    <th className="px-3 py-2 text-right">入金額</th>
                    <th className="px-3 py-2 text-left">注文番号</th>
                    <th className="px-3 py-2 text-left">氏名</th>
                    <th className="px-3 py-2 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedRows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-700">{formatStatus(row.check)}</td>
                      <td className="px-3 py-2 text-gray-700">{formatDate(row.depositdate)}</td>
                      <td className="px-3 py-2 text-gray-700">{row.name || "-"}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(row.incash)}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {row.orderId ? (
                          <Link to={`/admin/orders/${row.orderId}`} className="text-emerald-600 hover:underline">
                            {row.orderNumber}
                          </Link>
                        ) : (
                          row.orderNumber
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{row.userName || "-"}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setDetailTarget(row)}
                            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                          >
                            詳細
                          </button>
                          <button
                            type="button"
                            onClick={() => handleStartEdit(row)}
                            className="rounded border border-blue-200 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
                          >
                            編集
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row.id)}
                            disabled={deletingId === row.id}
                            className="rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
              <p className="text-xs text-gray-500">
                全{rows.length}件中 {startIndex + 1}〜{Math.min(startIndex + PAGE_SIZE, rows.length)}件を表示
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-md border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 disabled:opacity-50"
                >
                  前へ
                </button>
                <span className="text-xs text-gray-600">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-md border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 disabled:opacity-50"
                >
                  次へ
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {detailTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetailTarget(null)}>
          <div className="w-full max-w-xl rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">bankdata詳細</h3>
              <button type="button" onClick={() => setDetailTarget(null)} className="text-sm text-gray-500 hover:text-gray-700">
                閉じる
              </button>
            </div>
            <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
              <dt className="text-gray-500">ステータス</dt>
              <dd>{formatStatus(detailTarget.check)}</dd>
              <dt className="text-gray-500">入金日</dt>
              <dd>{formatDate(detailTarget.depositdate)}</dd>
              <dt className="text-gray-500">名義</dt>
              <dd>{detailTarget.name || "-"}</dd>
              <dt className="text-gray-500">入金額</dt>
              <dd>{formatCurrency(detailTarget.incash)}</dd>
              <dt className="text-gray-500">注文番号</dt>
              <dd>
                {detailTarget.orderId ? (
                  <Link to={`/admin/orders/${detailTarget.orderId}`} className="text-emerald-600 hover:underline">
                    {detailTarget.orderNumber || "-"}
                  </Link>
                ) : (
                  detailTarget.orderNumber || "-"
                )}
              </dd>
              <dt className="text-gray-500">氏名</dt>
              <dd>{detailTarget.userName || "-"}</dd>
            </dl>
          </div>
        </div>
      )}

      {editingTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditingTarget(null)}>
          <div className="w-full max-w-xl rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">paymentsRef編集</h3>
              <button type="button" onClick={() => setEditingTarget(null)} className="text-sm text-gray-500 hover:text-gray-700">
                閉じる
              </button>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700" htmlFor="payments-ref-input">
                paymentsRef（ドキュメントパス）
              </label>
              <input
                id="payments-ref-input"
                type="text"
                value={paymentsRefInput}
                onChange={(e) => setPaymentsRefInput(e.target.value)}
                placeholder="Order/{orderId}/payments/{paymentId}"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500">未設定の場合もここから新規保存できます（リファレンス形式）。</p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingTarget(null)}
                className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleSavePaymentsRef}
                disabled={saving}
                className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
